import { useState } from 'react';
import type { ClubResponse } from '../../types/api';

interface Props {
  clubs: ClubResponse[];
  activeClubId: number;
  onSelectClub?: (id: number) => void;
  onOpenCreateJoin?: () => void;
  onStartSession?: () => void;
}

export function ClubHeader({ clubs, activeClubId, onSelectClub, onOpenCreateJoin, onStartSession }: Props) {
  const [copied, setCopied] = useState(false);

  const activeClub = clubs.find((c) => c.id === activeClubId);
  if (!activeClub) return null;

  function handleCopyInviteCode() {
    if (!activeClub?.inviteCode) return;
    navigator.clipboard.writeText(activeClub.inviteCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="mb-2">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">{activeClub.name}</h1>
        {activeClub.role === 'Guest' && (
          <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-300">
            Read-only access
          </span>
        )}
        <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-sm font-medium text-slate-600 dark:text-slate-400">
          {activeClub.memberCount} {activeClub.memberCount === 1 ? 'member' : 'members'}
        </span>
      </div>

      {activeClub.inviteCode && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-slate-400">Invite code:</span>
          <code className="rounded-md bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-sm font-mono text-slate-700 dark:text-slate-300 tracking-widest">
            {activeClub.inviteCode}
          </code>
          <button
            onClick={handleCopyInviteCode}
            className={`rounded-md px-2.5 py-0.5 text-xs font-medium border transition-colors cursor-pointer ${
              copied
                ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400'
                : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}

      {onSelectClub && clubs.length > 1 && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {clubs.map((club) => (
            <button
              key={club.id}
              onClick={() => onSelectClub(club.id)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                club.id === activeClubId
                  ? 'bg-teal-600 dark:bg-teal-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {club.name}
            </button>
          ))}
        </div>
      )}

      {(onOpenCreateJoin || onStartSession) && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {onOpenCreateJoin && (
            <button
              onClick={onOpenCreateJoin}
              className="rounded-full border border-slate-300 dark:border-slate-600 px-4 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            >
              + Create / Join
            </button>
          )}
          {onStartSession && (
            <button
              onClick={onStartSession}
              className="rounded-full bg-teal-600 dark:bg-teal-500 text-white px-4 py-1.5 text-sm font-medium hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors cursor-pointer"
            >
              Start session
            </button>
          )}
        </div>
      )}
    </div>
  );
}
